<template>
  <ion-page>
    <main-header :back-href="userListHref"></main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <div
          v-if="!user"
          class="background-color ion-color-danger ion-padding ion-text-center"
        >
          The user was not found
        </div>
        <div v-else>
          <form @submit.prevent="handleEditSubmit()" data-cy="edit-form">
            <ion-grid>
              <ion-row>
                <ion-col size="12" size-md>
                  <ion-card>
                    <ion-card-header>
                      <ion-card-title>
                        Public Profile Information
                      </ion-card-title>
                    </ion-card-header>
                    <ion-card-content>
                      <p>
                        Customers will see this if this user is a runner.
                      </p>
                      <hr />
                      <ion-list>
                        <input-box
                          :validator="$v.displayName"
                          data-cy="display-name"
                          v-model="displayName"
                          :error-messages="{
                            maxLength: 'Please limit this to 255 characters.'
                          }"
                          maxlength="255"
                        >
                          Name
                        </input-box>
                        <ion-item lines="none">
                          <ion-label
                            position="stacked"
                            :color="$v.newAvatar.$error ? 'danger' : ''"
                          >
                            Avatar
                          </ion-label>
                        </ion-item>
                        <ion-item>
                          <ion-avatar slot="start">
                            <img
                              :src="user.photoURL"
                              alt="My Avatar"
                              class="avatar"
                              data-cy="avatar"
                            />
                          </ion-avatar>
                          <ion-button
                            v-if="!pickingNewAvatar"
                            size="small"
                            color="light"
                            @click="enablePickNewAvatar()"
                            >Choose another</ion-button
                          >
                          <div v-else class="w-100">
                            <ion-grid>
                              <ion-row>
                                <ion-col size="9">
                                  <ion-input
                                    ref="avatar"
                                    type="file"
                                    accept="image/jpeg, image/png"
                                    @change="handleAvatarChanged"
                                  />
                                </ion-col>
                                <ion-col size="3">
                                  <a
                                    href="#"
                                    @click.prevent="disablePickNewAvatar()"
                                    class="ion-float-right"
                                    >cancel</a
                                  >
                                </ion-col>
                              </ion-row>
                            </ion-grid>
                          </div>
                        </ion-item>
                        <ion-item
                          v-if="
                            $v.newAvatar.$error &&
                              !$v.newAvatar.imageUploadSizeMax
                          "
                          color="danger"
                        >
                          The maximum file upload size is limited to 1MB.
                        </ion-item>
                        <ion-item
                          v-if="
                            $v.newAvatar.$error &&
                              !$v.newAvatar.imageDimensionsAreSquare
                          "
                          color="danger"
                        >
                          The image must be exactly square.
                        </ion-item>
                      </ion-list>
                    </ion-card-content>
                  </ion-card>
                  <ion-list lines="full">
                    <input-box
                      :validator="$v.email"
                      :error-messages="{
                        required: 'Please enter an email address.',
                        email: 'Please enter a valid email address.'
                      }"
                      :required="true"
                      data-cy="email"
                      type="email"
                      v-model="email"
                    >
                      Email
                    </input-box>
                  </ion-list>
                </ion-col>
                <ion-col size="12" size-md>
                  <h3>Permissions</h3>
                  <ion-list>
                    <ion-item>
                      <ion-label
                        position="stacked"
                        :color="$v.role.$error ? 'danger' : ''"
                      >
                        Role
                      </ion-label>
                      <ion-select
                        @ionChange="role = $event.detail.value"
                        :value="role"
                        :color="$v.role.$error ? 'danger' : ''"
                        data-cy="role"
                      >
                        <ion-select-option
                          v-for="claim in CLAIMS"
                          v-bind:key="`claim-${claim}`"
                          :value="claim"
                          >{{ claim | ucfirst }}</ion-select-option
                        >
                      </ion-select>
                    </ion-item>
                    <ion-item
                      v-if="role === CLAIM_RUNNER || role === CLAIM_VENDOR"
                    >
                      <ion-label
                        position="stacked"
                        :color="$v.userLocations.$error ? 'danger' : ''"
                      >
                        Locations
                      </ion-label>
                      <ion-select
                        @ionChange="userLocations = $event.detail.value"
                        placeholder="None Selected"
                        multiple="true"
                      >
                        <ion-select-option
                          v-for="location in locations"
                          v-bind:key="`location-${location.id}`"
                          :value="location.id"
                          :selected="userLocations.includes(location.id)"
                        >
                          {{ location.name }}
                        </ion-select-option>
                      </ion-select>
                    </ion-item>
                    <ion-item
                      v-if="
                        $v.userLocations.$error && $v.userLocations.mustBeArray
                      "
                      color="danger"
                    >
                      This must be an array.
                    </ion-item>
                  </ion-list>

                  <h3>Payment Information</h3>
                  <div
                    v-if="!user.stripeCustomerId"
                    class="background-color ion-color-warning ion-padding ion-text-center"
                  >
                    This user does not have any payment information yet.
                  </div>
                  <ion-list v-else>
                    <ion-item>
                      <ion-label>Stripe ID</ion-label>
                      <ion-button
                        color="success"
                        size="small"
                        :href="stripeLink"
                        target="_blank"
                      >
                        {{ user.stripeCustomerId }}
                      </ion-button>
                    </ion-item>
                    <ion-item v-if="!stripeCards.length" color="warning"
                      >This user does not have any cards stored.</ion-item
                    >
                    <ion-item
                      v-else
                      v-for="stripeCard in stripeCards"
                      :key="`stripeCard-${stripeCard.id}`"
                    >
                      <ion-label>
                        {{ stripeCard.brand | ucfirst }}
                      </ion-label>
                      <ion-badge
                        v-if="stripeCard.isDefault"
                        color="light"
                        class="ion-margin-end"
                      >
                        primary
                      </ion-badge>
                      {{ stripeCard.last4 }}
                    </ion-item>
                  </ion-list>
                </ion-col>
              </ion-row>
            </ion-grid>
            <div class="ion-padding">
              <submit-button :submitting="submitting" data-cy="edit-submit">
                Save
              </submit-button>
            </div>
          </form>
          <div class="ion-padding">
            <ion-button
              data-cy="delete-button"
              color="danger"
              @click.prevent="deleteConfirm()"
              class="ion-margin-end"
            >
              Delete User
            </ion-button>
            <ion-button
              data-cy="revoke-token-button"
              color="danger"
              fill="outline"
              @click.prevent="revokeTokenConfirm()"
            >
              Log User Out
            </ion-button>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/**
 * Please note that this has a lot of copied code from my profile.  There are some differences, though.
 * In the future, if you come back to editing both sections, it might make sense to work on combining them then.
 */

import { required, email, maxLength } from "vuelidate/lib/validators";
import {
  imageUploadSizeMax,
  imageDimensionsAreSquare,
  mustBeArray
} from "@/validators/validators";
import {
  CLAIM_ADMIN,
  CLAIMS,
  CLAIM_RUNNER,
  CLAIM_VENDOR
} from "@/constants/claims";
import ImageMixin from "@/mixins/image";
import submittable from "@/mixins/submittable";
import User from "@/models/User";
import Location from "@/models/Location";
import StripeCard from "@/models/StripeCard";
import PlatformService from "@/services/Platform";
import errorAlert from "@/ErrorAlert";
import { mapGetters } from "vuex";

export default {
  mixins: [ImageMixin, submittable],

  title: "Edit User",

  data: function() {
    return {
      CLAIMS,
      CLAIM_VENDOR,
      CLAIM_RUNNER,
      loading: true,
      user: null,
      displayName: "",
      email: "",
      pickingNewAvatar: false,
      newAvatar: "",
      role: "",
      stripeCards: [],
      userLocations: [],
      locations: []
    };
  },

  methods: {
    deleteConfirm() {
      return this.$ionic.alertController
        .create({
          header: "Delete User",
          subHeader: this.user.email,
          message:
            "Please confirm that you'd like to delete this user.  <strong class='text-color ion-color-danger'>This can not be undone!</strong>",
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
              cssClass: "cancel-button"
            },
            {
              text: "Okay",
              cssClass: "okay-button",
              handler: () => {
                this.deleteUser();
              }
            }
          ]
        })
        .then(a => a.present());
    },

    deleteUser() {
      this.$firestore()
        .collection("users")
        .doc(this.$route.params.uid)
        .delete()
        .then(() => {
          this.successToast("The user has been deleted.");

          this.$router.push({ name: "Web: User List" });
        });
    },

    revokeUserToken() {
      let revokeUserTokens = this.$functions().httpsCallable(
        "revokeUserTokens"
      );

      revokeUserTokens({
        uid: this.user.uid
      })
        .then(() => {
          this.successToast("This user has been logged out of all devices.");
        })
        .catch(e => {
          errorAlert(e.message);
        });
    },

    revokeTokenConfirm() {
      return this.$ionic.alertController
        .create({
          header: "Log User Out",
          subHeader: this.user.email,
          message:
            "Please confirm that you'd like to log this user out.  All of their tokens (JWT) will be revoked and they will have to log in again on all devices.",
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
              cssClass: "cancel-button"
            },
            {
              text: "Okay",
              cssClass: "okay-button",
              handler: () => {
                this.revokeUserToken();
              }
            }
          ]
        })
        .then(a => a.present());
    },

    async handleEditSubmit() {
      this.$v.$touch();

      // right now touch is not promisified - I couldn't get it to work well anyway
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!this.$v.$invalid) {
        let updateParams = {};

        if (this.newAvatar) {
          let ref = this.$storage().ref();
          let uploadRef = ref.child(this.generateUserImagePath(this.user));

          const uploadTaskSnapshot = await uploadRef.put(this.newAvatar, {
            contentType: this.newAvatar.type
          });

          const downloadURL = await uploadTaskSnapshot.ref.getDownloadURL();
          updateParams.photoURL = downloadURL;
        }

        if (this.displayName !== this.user.displayName) {
          updateParams.displayName = this.displayName;
        }

        if (this.email !== this.user.email) {
          updateParams.email = this.email;
        }

        if (
          JSON.stringify(this.userLocations) !==
          JSON.stringify(this.user.userLocations)
        ) {
          updateParams.userLocations = this.userLocations;
        }

        if (!this.user.customClaims[this.role]) {
          let customClaims = {};
          customClaims[this.role] = true;
          updateParams.customClaims = customClaims;
        }

        if (Object.keys(updateParams).length !== 0) {
          updateParams.updated = this.$firestore.FieldValue.serverTimestamp();

          this.$firestore()
            .collection("users")
            .doc(this.user.id)
            .update(updateParams)
            .then(this.finishUpdate)
            .catch(() => {
              errorAlert("We were unable to update the user.");
            });
        } else {
          this.finishUpdate();
        }
      }
    },

    finishUpdate() {
      this.disablePickNewAvatar();
      this.successToast("This user has been updated successfully.");
    },

    enablePickNewAvatar() {
      this.pickingNewAvatar = true;

      // this is kind of hacky to get this to auto pop the element
      setTimeout(() => {
        document.querySelector("input[type=file]").click();
      }, 100); // next tick was too fast I guess
    },

    disablePickNewAvatar() {
      this.pickingNewAvatar = false;
      this.newAvatar = "";
    },

    handleAvatarChanged(event) {
      let files = event.target.files || event.dataTransfer.files;
      if (!files.length) {
        return;
      }
      this.newAvatar = files[0];
      this.$v.newAvatar.$reset();
    }
  },

  created() {
    if (!this.userIsAdmin) {
      return this.$router.push("/").catch(e => e);
    }

    Promise.all([
      this.$firestore()
        .collection("users")
        .withConverter(User.firebaseConverter)
        .doc(this.$route.params.uid)
        .get()
        .then(doc => {
          if (doc.exists) {
            this.user = doc.data();
            this.displayName = this.user.displayName;
            this.email = this.user.email;
            this.role = Object.keys(this.user.customClaims)[0];
            this.userLocations = this.user.userLocations || [];

            return this.$firestore()
              .collection("users")
              .doc(this.user.id)
              .collection("stripeCards")
              .withConverter(StripeCard.firebaseConverter)
              .get()
              .then(querySnapshot => {
                querySnapshot.forEach(doc => {
                  this.stripeCards.push(doc.data());
                });
              });
          }
        }),
      this.$firestore()
        .collection("locations")
        .withConverter(Location.firebaseConverter)
        .orderBy("name")
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(snapshot => {
            this.locations.push(snapshot.data());
          });
        })
    ]).finally(() => {
      this.loading = false;
    });
  },

  computed: {
    ...mapGetters("auth", ["userIsAdmin"]),

    userListHref() {
      return this.$router.resolve({
        name: "Web: User List"
      }).href;
    },

    stripeLink() {
      return (
        "https://dashboard.stripe.com/" +
        (PlatformService.isTest() ? "test" : "") +
        "/customers/" +
        this.user.stripeCustomerId
      );
    }
  },

  validations() {
    let validation = {
      email: {
        required,
        email
      },

      /**
       * Remember, this only works because we're doing web-only
       * In the future if this is for mobile, we'd need to get that working with a plugin
       * Also we're going to rely on the browser for validation - because these are admin
       */
      newAvatar: {
        imageUploadSizeMax,
        imageDimensionsAreSquare
      },

      role: {
        required
      },

      displayName: {
        maxLength: maxLength(255)
      },

      userLocations: {
        mustBeArray
      }
    };

    if (
      this.role === CLAIM_ADMIN ||
      this.role === CLAIM_RUNNER ||
      this.role === CLAIM_VENDOR
    ) {
      validation.displayName.required = required;
    }

    return validation;
  }
};
</script>
